import { PanelLeft, Search } from 'lucide-react';

import { PATIENT_PANEL_TABS_ANCHOR } from '@/app/hints';
import { Tabs, TabsList, TabsTrigger } from '@/shared/ui/shadcn/tabs';
import { PANEL_HEADER_HEIGHT_CLASS } from '@/shared/config';
import { toggleLeftSidebar } from '@/shared/stores/ui';
import { useHintAnchor } from '@/shared/ui/primitives';
import { ThemeToggle } from '@/shared/ui/theme';

import type { ReactNode } from 'react';

export interface PatientPanelTab {
  id: string;
  label: ReactNode;
}

interface PatientHeaderProps {
  /** Title/name to display (e.g., patient name) */
  title?: string;
  /** Currently active view tab */
  activeTab?: string;
  /** Tabs to display */
  tabs: PatientPanelTab[];
  /** Callback when a tab is selected */
  onTabChange?: (tabId: string) => void;
  /** Opens the workspace global search dialog */
  onOpenGlobalSearch?: () => void;
  /** Optional shortcut hint displayed in hover title */
  globalSearchShortcutLabel?: string;
  actions?: ReactNode;
}

/**
 * PatientHeader — header bar for the patient panel.
 *
 * Single row layout:
 * - Sidebar trigger (left)
 * - Title/name (left)
 * - View tabs (Master, Problem, Request)
 * - Theme toggle (right)
 */
export function PatientHeader({
  title,
  activeTab,
  tabs,
  onTabChange,
  onOpenGlobalSearch,
  globalSearchShortcutLabel = 'Cmd/Ctrl+F',
  actions,
}: PatientHeaderProps) {
  const tabsAnchorRef = useHintAnchor(PATIENT_PANEL_TABS_ANCHOR);

  return (
    <div
      className={`flex ${PANEL_HEADER_HEIGHT_CLASS} min-w-0 items-center justify-between gap-3 pl-2 pr-4`}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3 overflow-x-auto">
        <button
          type="button"
          onClick={toggleLeftSidebar}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
          aria-label="Toggle patient list"
        >
          <PanelLeft className="h-4 w-4" />
        </button>

        {title !== undefined && (
          <span className="shrink-0 whitespace-nowrap text-sm font-medium">{title}</span>
        )}
        {onOpenGlobalSearch !== undefined ? (
          <button
            type="button"
            onClick={onOpenGlobalSearch}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Open global patient search"
            title={`Global search (${globalSearchShortcutLabel})`}
          >
            <Search className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : null}

        <Tabs
          value={activeTab}
          onValueChange={(tabId) => onTabChange?.(tabId)}
          className="shrink-0"
        >
          <TabsList ref={tabsAnchorRef} aria-label="Patient views">
            {tabs.map((tab) => (
              <TabsTrigger key={tab.id} value={tab.id}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {actions}
        <ThemeToggle />
      </div>
    </div>
  );
}
